import React from "react";
//#region redux
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { DimensionEnums } from "models/enums";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FormFieldConstants } from "utils/constants";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
import { useDimensionMaps } from "components/common";
import { useInvoiceFormValidations } from "components/invoice";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance } from "utils/helpers";

function useSelfInvoiceFormValidations() {
  const { getProductFullSchema, getValidationSchemaProducts } = useInvoiceFormValidations();
  const { forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Invoice);

  const getValidationSchemaSelfInvoiceBasicProps = (): Record<keyof InvoiceFormTypes.SelfInvoice, Yup.AnySchema> => {
    return {
      id: Yup.string(),
      relatedInvoiceId: Yup.number(),
      ownerId: Yup.number(),
      currency: Yup.string(),
      isDimensionsAdvancedConfiguration: Yup.boolean(),
      isPeriodisationAdvancedConfiguration: Yup.boolean(),
      isPeriodisationByDate: Yup.boolean(),
      isEUCustomer: Yup.boolean(),
      isDomesticCustomer: Yup.boolean(),
      fromRecurring: Yup.boolean(),
      customer: Yup.object<AutocompleteListItem>({
        value: Yup.string(),
        id: Yup.number(),
      })
        .default(undefined)
        .required(TranslationKeys.Validations.Required),
      organizationNumber: Yup.string(),
      invoiceDate: Yup.date()
        .required(TranslationKeys.Validations.Required)
        .typeError(TranslationKeys.Validations.InvalidDate),
      oCRNumber: Yup.number(),
      products: Yup.array(),
    };
  };

  const getValidationSchema = React.useCallback(
    (isSoftValidation?: boolean) => {
      return Yup.object<InvoiceFormTypes.SelfInvoice>({
        ...getValidationSchemaSelfInvoiceBasicProps(),
        products: getProductFullSchema(isSoftValidation),
      });
    },
    [forcedDimensionMap]
  );

  const getTodoValidationSchema = React.useCallback(() => {
    return Yup.object<InvoiceFormTypes.SelfInvoiceTodo>({
      ...getValidationSchemaSelfInvoiceBasicProps(),
      products: Yup.array<InvoiceFormTypes.Product>()
        .of(
          Yup.object<InvoiceFormTypes.Product>({
            ...getValidationSchemaProducts(true, false),
            quantity: Yup.number()
              .required(TranslationKeys.Validations.Required)
              .min(0, LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", { min: "0" })),
            totalAmountExTax: Yup.number().required(TranslationKeys.Validations.Required),
          })
        )
        .required(TranslationKeys.Validations.AtLeastOneItem)
        .min(1, TranslationKeys.Validations.AtLeastOneItem),
      dimensions: Yup.array()
        .of(
          Yup.object<WintSuperkollAPIModel.DimensionData>({
            dimensionId: Yup.string(),
            amount: Yup.number(),
          })
        )
        .when(
          FormFieldConstants.Invoice.CreateBase.IsDimensionsAdvancedConfiguration,
          (
            [isDimensionsAdvancedConfiguration]: [boolean],
            dimensionsSchema: ArraySchema<WintSuperkollAPIModel.DimensionData>
          ) =>
            isDimensionsAdvancedConfiguration
              ? dimensionsSchema
              : dimensionsSchema.checkForcedDimensions(forcedDimensionMap)
        ),
    });
  }, [forcedDimensionMap]);

  return { getValidationSchema, getTodoValidationSchema };
}

export default useSelfInvoiceFormValidations;
